import { useMemo } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import classNames from 'classnames';
import { motion } from 'motion/react';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { getPositionByLabel, LoanRiskTypes, useLoanRiskType } from '@/shared/hooks/useLoanRiskType';
import { useResponsiveLabelSteps } from '@/shared/hooks/useResponsiveLebelSteps';
import DollarIcon from '@/shared/icons/flip-ui-kit/small/Dollar';
import { formatUsdValue, pipsToPercentString } from '@/shared/utils/numbers';

type CommonSliderProps = {
  min?: number;
  max?: number;
  thumbValue: number;
  bpsThresholds: number[];
  labelSteps?: number;
};

type BorrowSliderProps = CommonSliderProps & {
  initialValue?: number;
  setSliderValues?: (values: [number]) => void;
  onValueCommit?: (values: [number]) => void;
  ariaLabel?: string;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

const getLabelValues = (min: number, max: number, steps: number) => {
  const stepSize = (max - min) / (steps - 1);
  return Array.from({ length: steps }, (_, i) => min + stepSize * i);
};

const FakeThumb = ({
  loanToValueBps,
  targetLtvThresholdBps,
}: {
  loanToValueBps: number;
  targetLtvThresholdBps: number;
}) => (
  <span
    className="absolute top-[-3.5px] h-4 w-4 rounded-full border-2 border-cf-light-4 bg-white shadow focus:outline-none"
    style={{
      right: `${
        loanToValueBps === targetLtvThresholdBps
          ? 88
          : Math.max(
              0,
              Math.min(
                70,
                70 -
                  ((loanToValueBps - targetLtvThresholdBps) / (10000 - targetLtvThresholdBps)) * 70,
              ),
            )
      }px`,
    }}
  />
);

const Gap = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="13"
    height="10"
    viewBox="0 0 13 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M12.3008 1C10.0088 1 8.15044 2.79085 8.15039 5C8.15039 7.20919 10.0088 9 12.3008 9H0C2.29198 9 4.15039 7.20919 4.15039 5C4.15034 2.79085 2.29195 1 0 1H12.3008Z"
      fill={props.fill ?? '#161616'}
    />
    <rect y="9" width="12" height="1" fill={props.fill ?? '#161616'} />
    <rect width="12" height="1" fill={props.fill ?? '#161616'} />
  </svg>
);

const getTranslateClass = (index: number, itemsLength: number) => {
  if (index === 0) return 'translate-x-0';
  if (index === itemsLength - 1) return '-translate-x-full';
  return '-translate-x-1/2';
};

const SliderLabels = ({
  labelValues,
  min,
  max,
  bpsThresholds,
  readOnly = false,
  initialLtv = 0,
}: {
  labelValues: number[];
  min: number;
  max: number;
  bpsThresholds: number[];
  readOnly?: boolean;
  initialLtv?: number;
}) => {
  const { riskType } = useLoanRiskType(initialLtv);

  if (readOnly) {
    return (
      <div className="absolute left-0 top-full mt-3 w-full">
        {labelValues.map((val, index) => {
          const position = (100 * (val - min)) / (max - min);

          let content;
          if (index === labelValues.length - 1) {
            content = (
              <div className="flex items-center">
                <DollarIcon className="text-cf-green-3" /> {formatUsdValue(val, false, false)}
              </div>
            );
          } else {
            content = formatUsdValue(val, false, false);
          }

          return (
            <div
              key={val}
              className={`absolute top-0 ${getTranslateClass(index, labelValues.length)} text-center font-aeonikMedium text-12 text-cf-light-2`}
              style={{ left: `${position}%` }}
            >
              {content}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="absolute left-0 top-full mt-3 w-full">
      {LoanRiskTypes.map((type, index) => {
        // only show upcoming risk types unless no borrow position is open
        if (
          index <= LoanRiskTypes.indexOf(riskType) &&
          (riskType !== 'Conservative' || initialLtv !== 0)
        )
          return null;

        return (
          <div
            key={type}
            className={`absolute top-0 ${getTranslateClass(index, LoanRiskTypes.length)} text-center font-aeonikMedium text-12 text-cf-light-2`}
            style={{ left: `${getPositionByLabel(type, bpsThresholds[0], bpsThresholds[1])}%` }}
          >
            {type}
          </div>
        );
      })}
    </div>
  );
};

export const BorrowSlider = ({
  min = 0,
  max = 1000000,
  thumbValue,
  bpsThresholds,
  initialValue,
  setSliderValues,
  onValueCommit,
  labelSteps: initialLabelSteps = 4,
  ariaLabel,
  disabled = false,
  onFocus,
  onBlur,
}: BorrowSliderProps) => {
  const labelSteps = useResponsiveLabelSteps(initialLabelSteps);
  const labelValues = getLabelValues(min, max, labelSteps);
  const clampedInitialValue = Math.min(initialValue ?? 0, max);
  const isOverRiskThreshold = typeof initialValue === 'number' && initialValue >= bpsThresholds[0];

  return (
    <div className="relative mb-4 flex w-full flex-col">
      <div className="flex w-full items-center gap-x-1">
        {clampedInitialValue !== 0 && (
          <Tooltip
            pointer
            content="In use by open loans. Repay to free up capacity."
            fullWidth={isOverRiskThreshold}
          >
            <div
              className={classNames(
                'flex h-2 rounded-md bg-cf-gray-5',
                isOverRiskThreshold ? 'w-full' : 'w-[80px]',
              )}
            />
          </Tooltip>
        )}
        <SliderPrimitive.Root
          min={clampedInitialValue}
          max={max}
          disabled={disabled || !setSliderValues}
          aria-label={ariaLabel}
          value={[thumbValue]}
          onValueChange={setSliderValues}
          onValueCommit={onValueCommit}
          onPointerDown={() => onFocus?.()}
          onPointerUp={() => onBlur?.()}
          className={classNames(
            'relative flex w-full touch-none select-none items-center',
            isOverRiskThreshold ? 'hidden' : 'block',
          )}
        >
          <SliderPrimitive.Track
            className="relative h-2 flex-grow rounded-full bg-cf-gray-5"
            style={{
              background: `linear-gradient(90deg, ${clampedInitialValue < bpsThresholds[0] / 2 ? '#46DA93' : '#FCD774'} 0%, #F7931A 100%)`,
            }}
          />
          <SliderPrimitive.Thumb
            className={classNames(
              'group relative z-10 block h-4 w-4 cursor-pointer rounded-full border-2 border-cf-light-4 bg-white shadow focus:outline-none',
            )}
          >
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-cf-gray-4 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
              {pipsToPercentString(thumbValue)}
            </div>
          </SliderPrimitive.Thumb>
        </SliderPrimitive.Root>
        <Tooltip pointer content="High risk of liquidation.">
          <div
            className="flex h-2 w-[40px] rounded-md bg-cf-gray-5"
            style={{ background: '#EE4921' }}
          />
        </Tooltip>
        <Tooltip pointer content="Open loans enter liquidation.">
          <div
            className="flex h-2 w-[40px] rounded-md bg-cf-gray-5"
            style={{ background: '#F71A1A' }}
          />
        </Tooltip>
      </div>
      {/* hack: to avoid having multiple sliders, display a second readonly thumb when ltv is over 80% */}
      {isOverRiskThreshold && (
        <FakeThumb loanToValueBps={initialValue} targetLtvThresholdBps={bpsThresholds[0]} />
      )}
      <SliderLabels
        min={min}
        max={max}
        labelValues={labelValues}
        bpsThresholds={bpsThresholds}
        initialLtv={initialValue}
      />
    </div>
  );
};

export const BorrowSliderReadonly = ({
  min = 0,
  max = 1000000,
  thumbValue,
  bpsThresholds,
  labelSteps: initialLabelSteps = 4,
  gradient,
}: CommonSliderProps & { gradient: string }) => {
  const labelSteps = useResponsiveLabelSteps(initialLabelSteps);
  const labelValues = useMemo(() => getLabelValues(min, max, labelSteps), [min, max, labelSteps]);
  const percent = Math.max(0, Math.min(100, (100 * (thumbValue - min)) / (max - min)));
  const thumbPosition = `${percent}%`;

  return (
    <div className="relative mb-3 flex w-full flex-col">
      <div className="relative flex w-full items-center">
        <div
          className="relative h-[7.8px] flex-grow rounded-full bg-cf-gray-5"
          style={{ background: gradient }}
        >
          {bpsThresholds?.map((tick) => (
            <Gap
              key={tick}
              className={classNames('absolute bottom-[-1.1px]')}
              style={{
                left: pipsToPercentString(tick),
              }}
            />
          ))}
        </div>
        <motion.div
          className={classNames(
            'group absolute top-1/2 z-10 h-[12px] w-[5px] -translate-y-1/2 bg-[#D9D9D9]',
          )}
          initial={false}
          animate={{ left: thumbPosition }}
          transition={{
            duration: 0.6,
            ease: 'easeOut',
          }}
        >
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-cf-gray-4 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
            {formatUsdValue(thumbValue)}
          </div>
        </motion.div>
      </div>
      <SliderLabels
        min={min}
        max={max}
        labelValues={labelValues}
        bpsThresholds={bpsThresholds}
        readOnly
      />
    </div>
  );
};
